import React, { useState } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import "../../../assets/css/admin/Analytics.css";
import AdminLayout from "../../../layouts/AdminLayout/AdminLayout";

const Analytics = () => {
  const [selectedPeriod, setSelectedPeriod] = useState("7days");

  // User Growth Data
  const userGrowthData = [
    { month: "Jan", students: 120, mentors: 25 },
    { month: "Feb", students: 185, mentors: 32 },
    { month: "Mar", students: 245, mentors: 40 },
    { month: "Apr", students: 320, mentors: 48 },
    { month: "May", students: 410, mentors: 58 },
    { month: "Jun", students: 520, mentors: 68 },
    { month: "Jul", students: 650, mentors: 78 },
    { month: "Aug", students: 780, mentors: 89 },
    { month: "Sep", students: 920, mentors: 98 },
    { month: "Oct", students: 1050, mentors: 110 },
    { month: "Nov", students: 1180, mentors: 125 },
    { month: "Dec", students: 1234, mentors: 138 },
  ];

  // Course Completion Data
  const completionData = [
    { name: "Week 1", completed: 45, inProgress: 120, notStarted: 80 },
    { name: "Week 2", completed: 78, inProgress: 105, notStarted: 62 },
    { name: "Week 3", completed: 110, inProgress: 85, notStarted: 50 },
    { name: "Week 4", completed: 145, inProgress: 65, notStarted: 35 },
  ];

  // Revenue Data
  const revenueData = [
    { month: "Jan", revenue: 12500 },
    { month: "Feb", revenue: 15800 },
    { month: "Mar", revenue: 18200 },
    { month: "Apr", revenue: 22400 },
    { month: "May", revenue: 26700 },
    { month: "Jun", revenue: 31200 },
  ];

  // Popular Courses Distribution
  const coursesDistribution = [
    { name: "Web Development", value: 35, students: 432 },
    { name: "Data Science", value: 25, students: 308 },
    { name: "Mobile Apps", value: 20, students: 246 },
    { name: "UI/UX Design", value: 12, students: 148 },
    { name: "DevOps", value: 8, students: 98 },
  ];

  const COLORS = ["#781c2e", "#922636", "#a63042", "#ba3a4e", "#ce445a"];

  // Engagement Metrics
  const engagementMetrics = [
    { day: "Mon", sessions: 245, avgDuration: 42 },
    { day: "Tue", sessions: 268, avgDuration: 38 },
    { day: "Wed", sessions: 290, avgDuration: 45 },
    { day: "Thu", sessions: 310, avgDuration: 48 },
    { day: "Fri", sessions: 285, avgDuration: 40 },
    { day: "Sat", sessions: 195, avgDuration: 35 },
    { day: "Sun", sessions: 178, avgDuration: 32 },
  ];

  // Key Metrics Cards
  const keyMetrics = [
    {
      title: "Total Revenue",
      value: "$126,800",
      change: "+12.5%",
      trend: "up",
    },
    { title: "Active Users", value: "1,234", change: "+8.3%", trend: "up" },
    { title: "Completion Rate", value: "78%", change: "+5.2%", trend: "up" },
    {
      title: "Avg. Session Time",
      value: "42 min",
      change: "-2.1%",
      trend: "down",
    },
  ];

  return (
    <AdminLayout>
      <div className="analytics-container">
        {/* Header */}
        <div className="analytics-header">
          <div>
            <h2>Analytics & Reports</h2>
            <p className="analytics-subtitle">
              Track your platform performance and insights
            </p>
          </div>
          <div className="header-actions">
            <select
              className="period-selector"
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
            >
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="3months">Last 3 Months</option>
              <option value="year">This Year</option>
            </select>
            <button className="export-btn">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              Export Report
            </button>
          </div>
        </div>

        {/* Key Metrics Grid */}
        <div className="metrics-grid">
          {keyMetrics.map((metric, index) => (
            <div key={index} className="metric-card">
              <div className="metric-header">
                <span className="metric-title">{metric.title}</span>
                <span className={`metric-change ${metric.trend}`}>
                  {metric.trend === "up" ? "↑" : "↓"} {metric.change}
                </span>
              </div>
              <div className="metric-value">{metric.value}</div>
              <div className="metric-trend-line">
                <div className={`trend-bar ${metric.trend}`}></div>
              </div>
            </div>
          ))}
        </div>

        {/* Charts Grid */}
        <div className="charts-grid">
          {/* User Growth Chart */}
          <div className="chart-card full-width">
            <div className="chart-header">
              <h3>User Growth Trend</h3>
              <div className="chart-legend">
                <span className="legend-item">
                  <span
                    className="legend-dot"
                    style={{ backgroundColor: "#781c2e" }}
                  ></span>
                  Students
                </span>
                <span className="legend-item">
                  <span
                    className="legend-dot"
                    style={{ backgroundColor: "#4CAF50" }}
                  ></span>
                  Mentors
                </span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={userGrowthData}>
                <defs>
                  <linearGradient
                    id="colorStudents"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#781c2e" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#781c2e" stopOpacity={0.1} />
                  </linearGradient>
                  <linearGradient id="colorMentors" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4CAF50" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#4CAF50" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" stroke="#999" />
                <YAxis stroke="#999" />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="students"
                  stroke="#781c2e"
                  fillOpacity={1}
                  fill="url(#colorStudents)"
                />
                <Area
                  type="monotone"
                  dataKey="mentors"
                  stroke="#4CAF50"
                  fillOpacity={1}
                  fill="url(#colorMentors)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Course Completion Progress */}
          <div className="chart-card">
            <div className="chart-header">
              <h3>Course Completion Progress</h3>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={completionData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" stroke="#999" />
                <YAxis stroke="#999" />
                <Tooltip />
                <Legend />
                <Bar dataKey="completed" fill="#4CAF50" radius={[8, 8, 0, 0]} />
                <Bar
                  dataKey="inProgress"
                  fill="#FFC107"
                  radius={[8, 8, 0, 0]}
                />
                <Bar
                  dataKey="notStarted"
                  fill="#E0E0E0"
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Popular Courses Distribution */}
          <div className="chart-card">
            <div className="chart-header">
              <h3>Popular Courses Distribution</h3>
            </div>
            <div className="pie-chart-container">
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={coursesDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {coursesDistribution.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="course-stats">
                {coursesDistribution.map((course, index) => (
                  <div key={index} className="course-stat-item">
                    <div
                      className="course-stat-color"
                      style={{ backgroundColor: COLORS[index] }}
                    ></div>
                    <div className="course-stat-info">
                      <span className="course-stat-name">{course.name}</span>
                      <span className="course-stat-students">
                        {course.students} students
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Revenue Trend */}
          <div className="chart-card">
            <div className="chart-header">
              <h3>Revenue Trend</h3>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" stroke="#999" />
                <YAxis stroke="#999" />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="#781c2e"
                  strokeWidth={3}
                  dot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Weekly Engagement */}
          <div className="chart-card">
            <div className="chart-header">
              <h3>Weekly User Engagement</h3>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={engagementMetrics}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="day" stroke="#999" />
                <YAxis stroke="#999" />
                <Tooltip />
                <Bar dataKey="sessions" fill="#781c2e" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Insights Section */}
        <div className="insights-section">
          <h3>Key Insights</h3>
          <div className="insights-grid">
            <div className="insight-card">
              <div className="insight-icon success">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
              <div className="insight-content">
                <h4>Strong Growth</h4>
                <p>
                  Student enrollment increased by 35% this quarter, exceeding
                  targets.
                </p>
              </div>
            </div>
            <div className="insight-card">
              <div className="insight-icon warning">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                  <line x1="12" y1="9" x2="12" y2="13"></line>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
              </div>
              <div className="insight-content">
                <h4>Weekend Engagement Drop</h4>
                <p>
                  User activity drops by 40% on weekends. Consider weekend
                  campaigns.
                </p>
              </div>
            </div>
            <div className="insight-card">
              <div className="insight-icon info">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
              </div>
              <div className="insight-content">
                <h4>Top Performing Course</h4>
                <p>Web Development has the highest completion rate at 89%.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Analytics;
